import type { ApprovalAttributes } from '@/types/approval';

/** SAP 도급비 수수료 정산 — 레거시 appr_edit_forms_pay_contract_fee */
export const SAP_CONTRACT_FEE_TYPE = 167;

export const isSapContractFeeType = (apvType: number): boolean =>
  apvType === SAP_CONTRACT_FEE_TYPE;

/** 레거시 화면에 박힌 브랜드 7개 — 표시명은 157 과 같은 번역 키 */
export const SAP_CONTRACT_FEE_BRANDS = [
  { value: 'M', label: '엠엘비' },
  { value: 'I', label: '엠엘비 키즈' },
  { value: 'X', label: '디스커버리' },
  { value: 'A', label: 'STRETCH ANGELS' },
  { value: 'V', label: 'DUVETICA' },
  { value: 'W', label: '수프라' },
  { value: 'ST', label: '세르지오 타키니' },
] as const;

const START_YEAR = 2013;

/** 올해부터 2013년까지 내림차순 */
export const buildSapContractFeeYears = (now = new Date()): string[] => {
  const years: string[] = [];
  for (let y = now.getFullYear(); y >= START_YEAR; y -= 1)
    years.push(String(y));
  return years;
};

export const SAP_CONTRACT_FEE_MONTHS = Array.from({ length: 12 }, (_, i) =>
  String(i + 1),
);

export type SapContractFeeItem = {
  NAME1?: string;
  SHOP_NM?: string;
  ACTUALAMT?: string;
  ACTUSALESCOM?: string;
  TOTALSAMT?: string;
  TOTAL?: string;
  RATIO1?: string;
  REAL_AMT?: string;
  RATIO2?: string;
};

export type SapContractFeeHead = {
  T_ACTUALAMT?: string;
  T_ACTUSALESCOM?: string;
  T_TOTALSAMT?: string;
  T_TOTAL?: string;
  T_RATIO1?: string;
  T_REALAMT?: string;
  T_RATIO2?: string;
};

/** SAP 응답 원문(HEAD·ITEM) — 저장 attributes.data 에 손대지 않고 그대로 보관 */
export type SapContractFeeData = {
  HEAD?: object | null;
  ITEM?: object[] | null;
};

export type SapContractFeeLoaded = {
  year: number;
  month: number;
  data: SapContractFeeData;
};

export type SapContractFeeState = {
  brand: string;
  year: string;
  month: string;
  /** 데이터 호출로 받은 정산 명세. 없으면 상신 불가(레거시 data-load) */
  loaded: SapContractFeeLoaded | null;
};

export const createSapContractFeeState = (): SapContractFeeState => ({
  brand: '',
  year: '',
  month: '',
  loaded: null,
});

export type SapContractFeeDraftSource = {
  code: number;
  attributes: SapContractFeeLoaded;
};

export const SAP_CONTRACT_FEE_NO_DATA_MESSAGE =
  '해당 날짜의 데이터가 존재하지 않습니다.';

export const SAP_CONTRACT_FEE_NOT_LOADED_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** 데이터 호출 전 검사 — 레거시 btnShowData 순서 */
export const checkSapContractFeeQuery = (
  state: SapContractFeeState,
): string | null => {
  if (!state.brand) return '브랜드를 선택해주세요.';
  if (!state.year) return '연도를 선택해주세요.';
  if (!state.month) return '월을 선택해주세요.';
  return null;
};

export const getSapContractFeeItems = (
  data: SapContractFeeData | null | undefined,
): SapContractFeeItem[] => {
  const items = data?.ITEM;
  return Array.isArray(items) ? (items as SapContractFeeItem[]) : [];
};

export const getSapContractFeeHead = (
  data: SapContractFeeData | null | undefined,
): SapContractFeeHead | null => {
  const raw = data?.HEAD;
  const head: unknown = Array.isArray(raw) ? raw[0] : raw;
  return head && typeof head === 'object' ? (head as SapContractFeeHead) : null;
};

/** 도급업체명(NAME1)별 묶음 — 응답 순서 유지 */
export const groupSapContractFeeItems = (
  items: SapContractFeeItem[],
): [string, SapContractFeeItem[]][] => {
  const groups = new Map<string, SapContractFeeItem[]>();
  items.forEach((item) => {
    const key = item.NAME1 ?? '';
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  });
  return [...groups.entries()];
};

/** 레거시 Number(x).toLocaleString('ko-KR') */
export const formatSapContractFeeAmount = (value: unknown): string =>
  Number(value).toLocaleString('ko-KR');

export const SAP_CONTRACT_FEE_SUBTOTAL = '소계';

export const findSapContractFeeBrandLabel = (brand: string): string =>
  SAP_CONTRACT_FEE_BRANDS.find((b) => b.value === brand)?.label ?? '';

export const checkSapContractFeeBeforeSave = (
  state: SapContractFeeState | null | undefined,
): string | null =>
  state?.loaded && getSapContractFeeItems(state.loaded.data).length > 0
    ? null
    : SAP_CONTRACT_FEE_NOT_LOADED_MESSAGE;

export const buildSapContractFeeAttributes = (
  state: SapContractFeeState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.loaded
    ? {
        content,
        year: state.loaded.year,
        month: state.loaded.month,
        data: state.loaded.data,
      }
    : null;

export const parseSapContractFeeContent = (
  attributes: unknown,
): string | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const raw = (attributes as Record<string, unknown>).content;
  return typeof raw === 'string' && raw.trim() ? raw : null;
};

export const sapContractFeeKey = (state: SapContractFeeState | null): string =>
  state
    ? JSON.stringify([
        state.brand,
        state.year,
        state.month,
        state.loaded ? [state.loaded.year, state.loaded.month] : null,
        state.loaded?.data ?? null,
      ])
    : '';
